import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PlatformRegistrationApiService } from "@api-services/platform";
import DesktopLayout from "@components-shared/desktop/layout/DesktopLayout";
import { Loader } from "@components/loader/desktop";
import { HeaderType } from "@data-types/layout/headerTypeEnum";
import { ConfigType } from "@data-types/use-scroll-restoration/useScrollRestoration";
import { useScrollRestoration } from "@hooks/useScrollRestoration";
import { history } from "@state";
import { getCurrentCulture, setTabTitle } from "@utils";
import { analyticsTracker } from "@utils/analytics";

import {
	OnBoardingFooter,
	OnBoardingSlider,
} from "../registration-types/shared";

/**
	This component is used for "Send the email for verification" on administration (for retail and online user) and for completing the user registration process
	API endpoint: platform/gp/register/activate/{username}/{mailkey}
 */
export function RegistrationActivationPage() {
	const { t } = useTranslation("registration");
	const [activationState, setActivationState] = useState<boolean | null>(
		null
	);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [errorCode, setErrorCode] = useState<number>(0);

	useScrollRestoration({ type: ConfigType.Basic });

	useEffect(() => {
		setTabTitle(t("MEMBERSHIP.ACCOUNT_ACTIVATION.TITLE"));

		async function registrationActivation() {
			const activationToken = new URLSearchParams(
				history.location.search
			)?.get("mailkey");
			const username = new URLSearchParams(history.location.search)?.get(
				"username"
			);
			if (activationToken != null && username != null) {
				try {
					await PlatformRegistrationApiService.registrationActivate(
						username,
						activationToken
					);
					setActivationState(true);

					analyticsTracker.trackRegistrationCompleted();
				} catch (error) {
					console.error(error);
					try {
						const errorObj = await error.rawResponse.json();
						setErrorCode(errorObj?.errorCode);
						switch (errorObj?.errorCode) {
							// account already activated
							case 400116:
								setErrorMessage("VALIDATION.ALREADY_ACTIVATED");
								break;
							default:
								setErrorMessage(
									"COMMON.ERROR_MESSAGES.DEFAULT"
								);
						}
						setActivationState(false);
					} catch (e) {
						setActivationState(false);
					}
				}
			} else {
				setActivationState(false);
			}
		}
		registrationActivation();
		return () => {
			setActivationState(null);
		};
	}, []);

	return (
		<DesktopLayout
			headerType={HeaderType.EMPTY}
			mainClassName="at-RegistrationActivationPage l--main l--main--grow"
		>
			<Loader isLoading={activationState === null} />

			<section className="l--content l--content--full">
				<div className="l--content__main u-padd--reset">
					<div className="container container--med container--grow">
						<div className="card--onboarding card--onboarding--grow card--shadowy">
							<OnBoardingSlider />
							<div className="card--onboarding__form card--onboarding__form--center">
								<RegistrationActivationResult
									activationState={activationState}
									message={errorMessage}
									errorCode={errorCode}
								/>
							</div>
						</div>

						<OnBoardingFooter />
					</div>
				</div>
			</section>
		</DesktopLayout>
	);
}

function RegistrationActivationResult(props: {
	activationState: boolean | null;
	message: string;
	errorCode: number;
}) {
	const { t } = useTranslation("registration");

	if (props.activationState === null) {
		//activation is pending
		return (
			<>
				<div className="card--onboarding__header card--onboarding__header--vertical">
					<i className="u-icon u-icon--xxxlrg u-icon--pending u-icon--animated--bounce-in" />
					<div className="card--onboarding__title u-mar--top--sml">
						{t<string>("ACTIVATION_WAIT_TITLE")}
					</div>
				</div>

				<div className="card--onboarding__content card--onboarding__content--shrink u-type--center">
					<div>{t<string>("ACTIVATION_WAIT_MESSAGE")}</div>
				</div>
			</>
		);
	} else if (props.activationState) {
		//activation is successful
		return (
			<>
				<div className="card--onboarding__header card--onboarding__header--vertical">
					<i className="u-icon u-icon--xxxlrg u-icon--check-circle u-icon--animated--bounce-in u-color--sent--success" />
					<div className="card--onboarding__title u-mar--top--sml">
						{t<string>("ACTIVATION_SUCCESS_MESSAGE")}
					</div>
				</div>
				<div className="card--onboarding__content card--onboarding__content--shrink">
					<div className="u-mar--bottom--xsml">
						<div className="u-type--med u-type--wgt--medium">
							{t<string>(
								"MEMBERSHIP.ACCOUNT_ACTIVATION.PLAY_RESPONSIBLY"
							)}
						</div>
						<div>
							{t<string>(
								"MEMBERSHIP.ACCOUNT_ACTIVATION.RESPONSIBLE_GAMING_MESSAGE_ACTIVATED"
							)}
						</div>
					</div>

					<div className="u-type--wgt--medium">
						{t<string>(
							"MEMBERSHIP.ACCOUNT_ACTIVATION.WISHES_MESSAGE"
						)}
					</div>
				</div>

				<Link
					to={`/${getCurrentCulture()}/auth/login`}
					className="btn btn--base btn--block btn--accent"
				>
					{t<string>("COMMON.LOGIN_BUTTON")}
				</Link>
			</>
		);
	} else if (props.errorCode === 400116) {
		return (
			//account already activated
			<>
				<div className="card--onboarding__header card--onboarding__header--vertical">
					<i className="u-icon u-icon--xxxlrg u-icon--info-circle u-icon--animated--bounce-in" />
				</div>

				<div className="card--onboarding__content card--onboarding__content--shrink u-type--center">
					<div>{t<string>(props.message)}</div>
				</div>

				<Link
					to={`/${getCurrentCulture()}/auth/login`}
					className="btn btn--base btn--block btn--primary"
				>
					{t<string>("COMMON.LOGIN_BUTTON")}
				</Link>
			</>
		);
	}

	return (
		//activation failed
		<>
			<div className="card--onboarding__header card--onboarding__header--vertical">
				<i className="u-icon u-icon--xxxlrg u-icon--remove-circle u-icon--animated--bounce-in u-color--sent--danger" />
				<div className="card--onboarding__title u-mar--top--sml">
					{t<string>("COMMON.ERROR_MESSAGES.TITLE")}
				</div>
			</div>

			<div className="card--onboarding__content card--onboarding__content--shrink u-type--center">
				<div>{t<string>(props.message)}</div>
			</div>
		</>
	);
}
